import { formatCurrency } from '@if/core/utils'
import CheckIcon from '@mui/icons-material/Check'
import HelpIcon from '@mui/icons-material/Help'
import { Tooltip, Typography } from '@mui/material'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { useTranslation } from 'react-i18next'
import CollapsibleInvoiceRow from '../../Common/Table/CollapsibleInvoiceRow'
import InvoiceRow from '../../Common/Table/InvoiceRow'
import InvoiceTable from '../../Common/Table/InvoiceTable'
import {
  useLoanCalendarNearestOperationInformation,
  useLoanCurrentBalance,
  useLoanSummaryInformation,
} from '../LoanCalendar-hooks'

interface Props {
  simulation: CalendarSimulation
}

const LoanStatusCardContent = ({ simulation }: Props) => {
  const { t } = useTranslation()
  const nearestOperationInfo = useLoanCalendarNearestOperationInformation(simulation)
  const currentBalance = useLoanCurrentBalance(simulation)
  const summaryInformation = useLoanSummaryInformation(simulation)
  const dailyInterest = summaryInformation.dailyInterest ?? 0
  const lateCapital = summaryInformation?.pastduePrincipal ?? 0
  const pastdueAndAccruedInterest = summaryInformation?.pastdueAndAccruedInterest ?? 0
  const outstandingBalance = summaryInformation?.outstandingBalance ?? 0
  const amountRequiredForPayout = summaryInformation?.amountRequiredForPayout ?? 0
  
  const nsfBalance = nearestOperationInfo?.operationResultDto.nsfBalance ?? 0
  const otherBalance = nearestOperationInfo?.operationResultDto.otherBalance ?? 0
  const totalLateAndFee = pastdueAndAccruedInterest + nsfBalance + otherBalance
  const lateAccruedInterest = pastdueAndAccruedInterest - lateCapital
  
  const amountNotReplacedFragment =
    outstandingBalance > 0 ? (
      <Typography
        component="div"
        variant="inherit"
        sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'end', justifyContent: 'end' }}
      >
        {formatCurrency(outstandingBalance)}
      </Typography>
    ) : (
      <Typography
        component="div"
        variant="inherit"
        sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'end', justifyContent: 'end' }}
      >
        <CheckIcon color="success" />
        <span>{t('loan.payment.statusCard.status.upToDate')}</span>
      </Typography>
    )

  return (
    <InvoiceTable>
      <InvoiceRow
        label={
          <Typography variant="inherit" fontWeight="bold">
            {t('loan.payment.statusCard.total')}
          </Typography>
        }
        value={
          <Typography variant="inherit" fontWeight="bold">
            {formatCurrency(currentBalance)}
          </Typography>
        }
      />
      <InvoiceRow
        label={
          <Typography variant="inherit" fontStyle="italic">
            {t('loan.payment.statusCard.compensation')}
          </Typography>
        }
        value={formatCurrency(-99999)}
        actions={
          <Tooltip title={t('loan.payment.statusCard.compensationTooltip')}>
            <HelpIcon color="primary" fontSize="inherit" />
          </Tooltip>
        }
        spacing={1}
      />
      <InvoiceRow
        label={t('loan.payment.statusCard.dailyInterest')}
        value={formatCurrency(dailyInterest)}
        spacing={1}
      />
      <CollapsibleInvoiceRow label={t('loan.payment.statusCard.lateBalance')} value={formatCurrency(totalLateAndFee)}>
        <InvoiceRow
          label={t('loan.payment.statusCard.lateBalanceTooltip.principal')}
          value={formatCurrency(lateCapital)}
        />
        <InvoiceRow
          label={t('loan.payment.statusCard.lateBalanceTooltip.interest')}
          value={formatCurrency(lateAccruedInterest)}
        />
        <InvoiceRow label={t('loan.payment.statusCard.lateBalanceTooltip.nsfFee')} value={formatCurrency(nsfBalance)} />
        <InvoiceRow
          label={t('loan.payment.statusCard.lateBalanceTooltip.otherFee')}
          value={formatCurrency(otherBalance)}
        />
      </CollapsibleInvoiceRow>
      <InvoiceRow
        label={
          <Typography variant="inherit" fontWeight="bold">
            {t('loan.payment.statusCard.amountRequiredForPayout')}
          </Typography>
        }
        value={formatCurrency(amountRequiredForPayout)}
      />
      <InvoiceRow
        label={
          <Typography variant="inherit" fontWeight="bold">
            {t('loan.payment.statusCard.amountNotReplaced')}
          </Typography>
        }
        divider
        value={amountNotReplacedFragment}
      />
    </InvoiceTable>
  )
}

export default LoanStatusCardContent
